'use client'

import React, { useEffect, useState } from 'react'
import styles from './feedback.module.css'

export type AppLoadingVariant = 'fullscreen' | 'inline' | 'panel'

export interface AppLoadingScreenProps {
  /** Short status — keep calm and generic (no fake multi-step progress). */
  message?: string
  /** fullscreen = route/auth shell; inline = center in parent; panel = card-like well */
  variant?: AppLoadingVariant
  /** Spinner size (kept for API compat; desk surface uses activity bars) */
  size?: 'md' | 'lg' | 'xl'
  className?: string
  /** Accessible label (defaults to message or "Loading") */
  'aria-label'?: string
}

function formatDeskClock(d: Date): string {
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  const ss = String(d.getSeconds()).padStart(2, '0')
  return `${hh}:${mm}:${ss}`
}

/**
 * App bootstrap loading — **only after auth is valid**.
 *
 * While the session is still being established, use `AuthPendingScreen` instead.
 * Showing this before auth confuses users into thinking the product is ready.
 *
 * fintech-trading-ux: mono status strip + elevated panel (same language as AuthPending).
 */
export function AppLoadingScreen({
  message = 'Loading…',
  variant = 'fullscreen',
  size: _size = 'lg',
  className = '',
  'aria-label': ariaLabel,
}: AppLoadingScreenProps) {
  const label = ariaLabel || message || 'Loading'
  const [clock, setClock] = useState('--:--:--')
  useEffect(function () {
    function tick() { setClock(formatDeskClock(new Date())) }
    tick()
    const id = window.setInterval(tick, 1000)
    return function () { window.clearInterval(id) }
  }, [])

  const rootClass = [
    styles.appLoading,
    variant === 'fullscreen' ? styles.appLoadingFullscreen : '',
    variant === 'inline' ? styles.appLoadingInline : '',
    variant === 'panel' ? styles.appLoadingPanel : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div
      className={rootClass}
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={label}
      data-variant={variant}
    >
      {variant === 'fullscreen' ? (
        <div className={styles.authPendingAmbient} aria-hidden />
      ) : null}

      <div className={styles.authPendingPanel}>
        <header className={styles.authPendingHeader}>
          <div className={styles.authPendingBrand}>
            <span className={styles.authPendingMark} aria-hidden>
              M
            </span>
            <div className={styles.authPendingBrandCopy}>
              <span className={styles.authPendingBrandName}>Mechagora</span>
              <span className={styles.authPendingBrandMeta}>WORKSPACE · DESK</span>
            </div>
          </div>
          <span className={styles.authPendingLive} title="Workspace boot in progress">
            <span className={styles.authPendingLiveDot} aria-hidden />
            BOOT
          </span>
        </header>

        <div className={styles.authPendingStrip} aria-hidden>
          <span className={styles.authPendingLabel}>PHASE</span>
          <span className={styles.authPendingValue} data-state="restore">
            WORKSPACE
          </span>
          <span className={styles.authPendingLabel}>STATUS</span>
          <span className={styles.authPendingValue} data-state="restore">
            OPENING
          </span>
          <span className={styles.authPendingLabel}>CLOCK</span>
          <time
            className={styles.authPendingValue}
            suppressHydrationWarning
          >
            {clock}
          </time>
        </div>

        <div className={styles.authPendingBody}>
          <div className={styles.authPendingActivity} aria-hidden>
            <span className={styles.authPendingBar} />
            <span className={styles.authPendingBar} />
            <span className={styles.authPendingBar} />
            <span className={styles.authPendingBar} />
            <span className={styles.authPendingBar} />
          </div>
          <p className={styles.authPendingMessage}>{message}</p>
          <p className={styles.authPendingHint}>
            Session is valid — mounting desk shell. Not a session check.
          </p>
        </div>

        <footer className={styles.authPendingFooter} aria-hidden>
          <span>APP TIER</span>
          <span className={styles.authPendingFooterSep}>·</span>
          <span>AUTH COMPLETE</span>
        </footer>
      </div>
    </div>
  )
}

export default AppLoadingScreen
